import { forwardRef, memo, type ReactNode } from 'react';
import map from 'lodash/map';
import size from 'lodash/size';
import isString from 'lodash/isString';
import { FormattedMessage } from 'react-intl';
// Material UI imports
import TableContainer, { type TableContainerProps } from '@mui/material/TableContainer';
import Table, { type TableProps } from '@mui/material/Table';
import TableHead from '@mui/material/TableHead';
import TableBody from '@mui/material/TableBody';
import TableRow from '@mui/material/TableRow';
import TableCell from '@mui/material/TableCell';
import Alert from '@mui/material/Alert';
// local imports
import type { Values } from '../helpers/intl';
import FetchFailedAlert from './FetchFailedAlert';
import LoadingPlaceholder from './LoadingPlaceholder';
import ReloadingOverlay from './ReloadingOverlay';

export interface DataTableRow {
  values: ReactNode[];
  selected: boolean;
}

type DataTableProps = {
  readonly data?: DataTableRow[] | null;
  readonly titles: ReactNode[];
  readonly titleValues?: Values;
  readonly empty: string;
  readonly pending?: boolean | null;
  readonly failed?: boolean | null;
  readonly lastLeftAlignedTitle?: number;
  readonly tableSize?: TableProps['size'];
  readonly className?: string;
  readonly containerSx?: TableContainerProps['sx'];
  readonly sx?: TableProps['sx'];
  readonly withReloading?: boolean | null;
};

const DataTable = forwardRef<HTMLDivElement, DataTableProps>(({
  data,
  titles,
  titleValues,
  empty,
  pending = false,
  failed = false,
  lastLeftAlignedTitle = 0,
  tableSize = 'small',
  className,
  containerSx,
  sx,
  withReloading = false
}, ref) => {
  const loading = withReloading ? pending && !data : pending;
  const reloading = Boolean(withReloading && pending && data && size(data) >= 1);
  return (
    <ReloadingOverlay reloading={reloading}>
      <TableContainer ref={ref} sx={containerSx}>
        <Table size={tableSize} className={className} sx={sx}>
          <TableHead>
            <TableRow>
              {map(titles, (title, index) => (
                <TableCell
                    key={index}
                    align={index <= lastLeftAlignedTitle ? 'left' : 'center'}
                >
                  {isString(title) && size(title) >= 1 ? (
                    <FormattedMessage id={title} defaultMessage={title} values={titleValues}/>
                  ) : title}
                </TableCell>
              ))}
            </TableRow>
          </TableHead>
          <TableBody>
            {loading || failed || !data || size(data) < 1
            ? (
              <TableRow>
                <TableCell padding="none" colSpan={size(titles)}>
                  {(failed && <FetchFailedAlert flat/>) ||
                  ((loading || !data) && <LoadingPlaceholder flat/>) || (
                    <Alert severity="info" variant="standard">
                      <FormattedMessage id={empty} defaultMessage={empty}/>
                    </Alert>
                  )}
                </TableCell>
              </TableRow>
              )
            : map(data, ({ values, selected }, rowIndex) => (
              <TableRow
                  key={rowIndex}
                  selected={selected}
              >
                {map(titles, (_title, index) => (
                  <TableCell
                      key={index}
                      align={index <= lastLeftAlignedTitle ? 'left' : 'center'}
                  >
                    {values[index]}
                  </TableCell>
                ))}
              </TableRow>
              )
            )}
          </TableBody>
        </Table>
      </TableContainer>
    </ReloadingOverlay>
  );
});

DataTable.displayName = 'DataTable';

export default memo(DataTable);
